import { useEffect, useState } from "react";
import { ApiError, fetchDecisionProof, fetchTransferOptions, runTransfer } from "../api";
import { completeMission } from "../game";
import type {
  DecisionProof,
  DemoRunResult,
  TransferChoice,
  TransferOptions,
  TransferSelection,
} from "../types";
import { DecisionFlow } from "./DecisionFlow";
import { DecisionProofCard } from "./DecisionProofCard";

/**
 * Compose a transfer from fixed choices and watch what the system does with it.
 *
 * Every option is fetched from the server rather than hardcoded here, so the form can never
 * offer something the API would reject. There is no text field anywhere: the visitor picks,
 * the server builds the transaction.
 *
 * The result is shown twice on purpose. An engineer reads "HTTP 503" and knows what it
 * means; everyone else needs "the money did not move, try again". Showing both side by side
 * is what makes the engineering claim legible without weakening it.
 */

interface CustomerView {
  tone: "ok" | "waiting" | "error";
  headline: string;
  detail: string;
}

/** Preset combinations, described by the risk signals they contain rather than by the
 *  verdict they produce. The verdict comes from the server - a label that predicted it
 *  would quietly become a lie the moment the threshold or the backend changed. */
const PRESETS: {
  name: string;
  why: string;
  selection: { recipient: string; amount: string; time_of_day: string; channel: string };
}[] = [
  {
    name: "😊 평범한 송금",
    why: "아는 사람에게, 낮 시간에, 적은 돈을. 위험 신호가 하나도 없어요. AI가 어떻게 할까요?",
    selection: {
      recipient: "known",
      amount: "small",
      time_of_day: "afternoon",
      channel: "MOBILE",
    },
  },
  {
    name: "💰 금액만 큰 송금",
    why: "아는 사람인데 3,000만원. 위험 신호가 금액 딱 하나예요. 이것만으로 잡힐까요?",
    selection: {
      recipient: "known",
      amount: "huge",
      time_of_day: "afternoon",
      channel: "MOBILE",
    },
  },
  {
    name: "🌙 새벽에 처음 보는 계좌로",
    why: "처음 보내는 계좌 + 새벽 3시. 계좌를 도둑맞았을 때 실제로 자주 나타나는 조합이에요.",
    selection: {
      recipient: "new",
      amount: "large",
      time_of_day: "night",
      channel: "MOBILE",
    },
  },
  {
    name: "🚨 위험 신호 총집합",
    why: "처음 보는 계좌 + 3,000만원 + 새벽 3시 + 외부 연동. 위험 신호 4개가 한꺼번에! 사기꾼이 된 기분으로 눌러보세요.",
    selection: { recipient: "new", amount: "huge", time_of_day: "night", channel: "API" },
  },
];

function customerView(result: DemoRunResult): CustomerView {
  if (result.status === "failed") {
    return {
      tone: "error",
      headline: "😥 송금하지 못했어요",
      detail:
        "시스템에 문제가 생겨 처리하지 못했어요. 돈은 한 푼도 빠져나가지 않았고, 이 송금은 없던 일이 됐어요. 잠시 후 다시 시도해 주세요.",
    };
  }
  const decision = result.decisions[result.decisions.length - 1];
  if (!decision) {
    return { tone: "error", headline: "처리 결과 없음", detail: "판정 결과가 없습니다." };
  }
  if (decision.outcome === "APPROVE") {
    return {
      tone: "ok",
      headline: "✅ 송금 완료!",
      detail: "AI가 안전하다고 판단해서, 아무도 기다리게 하지 않고 바로 통과시켰어요.",
    };
  }
  return {
    tone: "waiting",
    headline: "🕵️ 잠깐! 확인이 필요해요",
    detail:
      "AI가 위험 신호를 발견해서 은행 담당자에게 넘겼어요. 진짜 사기라면 여기서 막히고, 아니라면 확인 후 처리돼요.",
  };
}

function bankView(result: DemoRunResult): string {
  if (result.status === "failed") {
    return (
      `판정을 장부에 확정하지 못함 → 성공 응답을 만들지 않고 오류(HTTP ${result.http_status})로 답함. ` +
      "가장 위험한 실패는 '저장 안 됐는데 됐다고 답하는 것'이라 그 경로를 아예 막아뒀습니다. " +
      "장부에 아무것도 남지 않았으므로 고객이 다시 눌러도 이중 송금되지 않습니다."
    );
  }
  const decision = result.decisions[result.decisions.length - 1];
  if (!decision) return "판정 없음";
  if (decision.degraded) {
    return `구성요소 장애(${decision.reason_codes.join(", ")}) → 자동 승인으로 흘리지 않고 REVIEW로 전환. 사람 개입 필요.`;
  }
  if (decision.outcome === "APPROVE") {
    return `위험 점수 ${decision.risk_score?.toFixed(4)} < 임계값 ${result.review_threshold.toFixed(4)} → 자동 승인. 사람 개입 0초.`;
  }
  return `위험 점수 ${decision.risk_score?.toFixed(4)} ≥ 임계값 ${result.review_threshold.toFixed(4)} → 검토 대기열로. 담당자 1건 소모.`;
}

/** The line that makes this a story about operations rather than about a model: every
 *  decision either consumed a person's attention or did not. */
function humanCost(result: DemoRunResult): string {
  if (result.status === "failed") {
    return "🙌 사람 손: 0건 (판정 자체가 확정되지 않아 대기열에 들어가지 않음)";
  }
  const decision = result.decisions[result.decisions.length - 1];
  if (!decision) return "사람 손: —";
  if (decision.replayed) {
    return "🙌 사람 손: 0건 — 같은 요청이 다시 와도 이미 내린 판정을 재사용해요.";
  }
  return decision.outcome === "APPROVE"
    ? "🙌 사람 손: 0건 — 이 거래는 은행 직원 아무도 보지 않고 끝났어요."
    : "🧑‍💼 사람 손: 1건 — 담당자가 직접 봐야 하는 일이 하나 늘었어요. 이게 쌓이면 곧 인건비예요.";
}

/**
 * The anti-fake panel: the strongest skepticism a visitor can have is "this screen just
 * made the verdict up". The answer is not a promise but an exit: the exact request that
 * was sent, the exact JSON that came back, and a copy-pastable curl that reproduces the
 * verdict with this frontend switched off entirely.
 */
/** What each load-bearing field of the raw reply means - so the JSON is evidence a
 *  visitor can actually read, not a wall of text they scroll past. */
const JSON_LEGEND: { field: string; meaning: string }[] = [
  {
    field: "decisions[].outcome",
    meaning: "판정 결과. APPROVE=통과 · REVIEW=사람에게 · (중간 위험이면 본인 확인을 거침)",
  },
  { field: "decisions[].risk_score", meaning: "AI가 매긴 위험 점수 (0에 가까울수록 안전)" },
  {
    field: "decisions[].reason_codes",
    meaning: "왜 그렇게 판정했는지 이유 코드. STEP_UP_PASSED가 있으면 본인 확인을 통과한 것",
  },
  {
    field: "run_id · decision_id",
    meaning:
      "판정마다 새로 발급되는 장부 번호. 같은 송금을 다시 보내면 번호가 달라져요 — 미리 녹화해 둔 답이 아니라는 증거",
  },
  {
    field: "decisions[].versions",
    meaning: "이 판정을 내린 모델·피처·정책의 버전. 나중에 '누가 판정했나'를 추적하는 감사 기록",
  },
];

function RawServerProof({
  selection,
  result,
}: {
  selection: TransferSelection;
  result: DemoRunResult;
}) {
  const [copied, setCopied] = useState(false);
  const [os, setOs] = useState<"unix" | "windows">("unix");

  const endpoint = `${window.location.origin}/v1/demo/transfer`;
  const body = JSON.stringify(selection);
  // cmd.exe has no single quotes, so the Windows variant escapes the JSON's double
  // quotes instead. `curl.exe` (not `curl`) dodges PowerShell's Invoke-WebRequest alias.
  const curl =
    os === "unix"
      ? `curl -s -X POST ${endpoint} \\\n` +
        `  -H "Content-Type: application/json" \\\n` +
        `  -d '${body}'`
      : `curl.exe -s -X POST ${endpoint} ` +
        `-H "Content-Type: application/json" ` +
        `-d "${body.replace(/"/g, '\\"')}"`;

  async function copyCurl() {
    try {
      await navigator.clipboard.writeText(curl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access denied - the command is still selectable by hand.
    }
  }

  return (
    <details className="raw-proof">
      <summary className="raw-proof-summary">
        🔬 &quot;이거 화면이 꾸며낸 거 아니야?&quot; — 서버 원본으로 검증하기
      </summary>
      <p>
        좋은 의심이에요. 이 화면은 아래 명령과 <strong>완전히 같은 요청</strong>을 서버에
        보내고, 서버가 돌려준 JSON을 그대로 그렸을 뿐이에요. 못 믿겠다면 직접 검증해
        보세요:
      </p>
      <ol className="raw-proof-steps">
        <li>아래 명령을 복사하세요 (이 사이트는 이제 안 쓸 거예요)</li>
        <li>
          터미널에 붙여넣고 실행하세요 — 맥은 <strong>터미널</strong>, 윈도우는{" "}
          <strong>명령 프롬프트</strong>나 <strong>PowerShell</strong>
        </li>
        <li>
          돌아온 답장의 <code>outcome</code>과 <code>risk_score</code>를 이 화면과
          비교하세요. 같은 FDS 서버가 판정하니 같은 결과가 나옵니다
        </li>
      </ol>
      <div className="raw-proof-block">
        <div className="raw-proof-head">
          <span>📮 서버에 직접 물어보는 명령</span>
          <span className="raw-proof-actions">
            <button
              type="button"
              className={os === "unix" ? "active" : ""}
              onClick={() => setOs("unix")}
            >
              맥·리눅스
            </button>
            <button
              type="button"
              className={os === "windows" ? "active" : ""}
              onClick={() => setOs("windows")}
            >
              윈도우
            </button>
            <button type="button" className="raw-proof-copy" onClick={copyCurl}>
              {copied ? "복사됨 ✓" : "📋 복사"}
            </button>
          </span>
        </div>
        <pre>{curl}</pre>
      </div>
      <div className="raw-proof-block">
        <div className="raw-proof-head">
          <span>📄 방금 서버가 보낸 원본 답장 (JSON)</span>
        </div>
        <pre>{JSON.stringify(result, null, 2)}</pre>
      </div>
      <div className="raw-proof-legend">
        <strong>🔎 답장 읽는 법</strong>
        <ul>
          {JSON_LEGEND.map((item) => (
            <li key={item.field}>
              <code>{item.field}</code> — {item.meaning}
            </li>
          ))}
        </ul>
      </div>
      <p className="muted">
        이 서버가 할 수 있는 일 전체는{" "}
        <a href="/docs" target="_blank" rel="noreferrer">
          API 문서(/docs)
        </a>
        에서 브라우저로 직접 눌러볼 수 있어요. 이 사이트는 저 API에 달린 여러 창구 중
        하나일 뿐입니다.
      </p>
    </details>
  );
}

function ChoiceRow({
  label,
  choices,
  selected,
  onSelect,
  disabled,
}: {
  label: string;
  choices: TransferChoice[];
  selected: string;
  onSelect: (value: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="choice-row">
      <span className="choice-label">{label}</span>
      <div className="choice-buttons">
        {choices.map((choice) => (
          <button
            key={choice.value}
            type="button"
            disabled={disabled}
            className={`choice ${selected === choice.value ? "selected" : ""}`}
            onClick={() => onSelect(choice.value)}
            title={choice.hint ?? undefined}
          >
            {choice.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function TransferSimulator() {
  const [options, setOptions] = useState<TransferOptions | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [recipient, setRecipient] = useState("known");
  const [amount, setAmount] = useState("medium");
  const [timeOfDay, setTimeOfDay] = useState("afternoon");
  const [channel, setChannel] = useState("MOBILE");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<DemoRunResult | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [activePreset, setActivePreset] = useState<string | null>(null);
  // Captured at send time: the visitor can change the form after sending, and the raw
  // proof must show the request that produced this result, not the current form state.
  const [sentSelection, setSentSelection] = useState<TransferSelection | null>(null);
  // The read-back proof is a SECOND request made with the freshly issued decision_id.
  // The confirmation card renders exclusively from these states - never from `result`.
  const [proof, setProof] = useState<DecisionProof | null>(null);
  const [proofLoading, setProofLoading] = useState(false);
  const [proofError, setProofError] = useState<string | null>(null);

  useEffect(() => {
    fetchTransferOptions()
      .then(setOptions)
      .catch((err: unknown) => {
        setLoadError(
          err instanceof ApiError ? err.message : "송금 옵션을 불러오지 못했습니다.",
        );
      });
  }, []);

  async function handleSend() {
    setRunning(true);
    setRunError(null);
    setResult(null);
    const selection: TransferSelection = {
      recipient,
      amount,
      time_of_day: timeOfDay,
      channel,
    };
    setSentSelection(selection);
    setProof(null);
    setProofError(null);
    try {
      const run = await runTransfer(selection);
      setResult(run);
      // Tells the live-proof strip to re-count the ledger right away, so the visitor
      // watches their own decision bump the number instead of waiting for the next poll.
      window.dispatchEvent(new CustomEvent("safefds:decision"));
      // A real decision came back from the real server: mission 1 verified.
      completeMission("try-transfer");
      const decision = run.decisions[run.decisions.length - 1];
      if (decision) {
        // The "10초 검증" read-back: a separate GET with the fresh decision_id, answered
        // by a new DB session on the server. Its failure is shown as a failure.
        setProofLoading(true);
        try {
          setProof(await fetchDecisionProof(decision.decision_id));
        } catch (proofErr) {
          setProofError(
            proofErr instanceof ApiError
              ? proofErr.message
              : "조회 요청이 서버에 닿지 못했습니다.",
          );
        } finally {
          setProofLoading(false);
        }
      }
    } catch (err) {
      setRunError(err instanceof ApiError ? err.message : "송금을 처리하지 못했습니다.");
    } finally {
      setRunning(false);
    }
  }

  if (loadError) {
    return (
      <section className="card">
        <h3>직접 송금해 보기</h3>
        <p className="error-text">{loadError}</p>
      </section>
    );
  }
  if (!options) {
    return (
      <section className="card">
        <h3>직접 송금해 보기</h3>
        <p className="muted">불러오는 중…</p>
      </section>
    );
  }

  const view = result ? customerView(result) : null;

  return (
    <section className="card">
      <h3>🎮 직접 송금해 보기</h3>
      <p className="warn-banner">
        가상의 데모입니다. 실제 은행도, 실제 계좌도, 실제 돈도 아니에요. 마음껏 눌러보세요!
      </p>
      <p>
        이번엔 당신이 <strong>송금을 보내는 사람</strong>입니다. 아래에서 네 가지를 골라 송금
        버튼을 누르면, AI가 <strong>순식간에</strong> 통과시킬지 잡을지 결정해요. 어떤 조합이면
        잡히는지 실험해 보세요.
      </p>

      <div className="preset-row">
        <span className="choice-label">이렇게 해보세요</span>
        <div className="choice-buttons">
          {PRESETS.map((preset) => (
            <button
              key={preset.name}
              type="button"
              className="choice preset"
              disabled={running}
              title={preset.why}
              onClick={() => {
                setRecipient(preset.selection.recipient);
                setAmount(preset.selection.amount);
                setTimeOfDay(preset.selection.time_of_day);
                setChannel(preset.selection.channel);
                setActivePreset(preset.name);
              }}
            >
              {preset.name}
            </button>
          ))}
        </div>
      </div>
      {activePreset && (
        <p className="preset-why">
          {PRESETS.find((p) => p.name === activePreset)?.why} 아래 &quot;송금하기&quot;를 눌러
          결과를 확인해 보세요.
        </p>
      )}

      <div className="transfer-form">
        <ChoiceRow
          label="받는 사람"
          choices={options.recipients}
          selected={recipient}
          onSelect={setRecipient}
          disabled={running}
        />
        <ChoiceRow
          label="금액"
          choices={options.amounts}
          selected={amount}
          onSelect={setAmount}
          disabled={running}
        />
        <ChoiceRow
          label="시간"
          choices={options.times}
          selected={timeOfDay}
          onSelect={setTimeOfDay}
          disabled={running}
        />
        <ChoiceRow
          label="방법"
          choices={options.channels}
          selected={channel}
          onSelect={setChannel}
          disabled={running}
        />
        <button className="send-button" onClick={handleSend} disabled={running}>
          {running ? "처리 중…" : "송금하기"}
        </button>
      </div>

      {runError && <p className="error-text">{runError}</p>}

      <DecisionFlow result={result} running={running} />

      {result && view && (
        <div className="dual-view">
          <div className={`view-card customer ${view.tone}`}>
            <span className="view-tag">고객이 보는 화면</span>
            <strong>{view.headline}</strong>
            <p>{view.detail}</p>
          </div>
          <div className="view-card bank">
            <span className="view-tag">은행 내부에서 벌어지는 일</span>
            <p>{bankView(result)}</p>
            <p className="human-cost">{humanCost(result)}</p>
          </div>
        </div>
      )}

      {result && (
        <DecisionProofCard proof={proof} loading={proofLoading} error={proofError} />
      )}

      {result && sentSelection && (
        <RawServerProof selection={sentSelection} result={result} />
      )}
    </section>
  );
}
